/**
 * PlannerSettings.jsx — shop-wide planner config (UI Change Brief §4).
 *
 * Reads GET /planner/config, lets an operator edit fields, and saves via
 * PATCH /planner/config sending ONLY changed fields (brief §4.1). A reset
 * button restores defaults via POST /planner/config/reset.
 *
 * Form is split (brief §4.2): everyday "Tolerances & Objective" fields plus an
 * Advanced section (the three search knobs) collapsed by default. Field specs
 * (control, range, label) come from CONFIG_FIELDS in planner.js. Ranges are
 * mirrored client-side so out-of-range values don't round-trip to a 422.
 *
 * Scope note (brief §4.3): these are SHOP-WIDE defaults affecting every future
 * plan. Per-job overrides live in the plan payload, not here.
 *
 * Controlled by App.js:
 *   config        — current config object (or null while loading).
 *   draft         — editable copy of config.
 *   setDraft      — setter for the draft.
 *   busy, error, savedAt — status.
 *   onSave(), onReset(), onReload() — actions.
 */

import React from 'react';
import { CONFIG_FIELDS } from './planner';
import { useTheme } from './theme';

function Btn({ children, onClick, primary = true, disabled = false }) {
  const { P, FN } = useTheme();
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily: FN, fontWeight: 500, cursor: disabled ? 'default' : 'pointer',
      fontSize: 12.5, padding: '8px 16px', borderRadius: 6, letterSpacing: 0.1,
      border: primary ? 'none' : `0.5px solid ${P.rule}`,
      background: primary ? P.ink : 'transparent', color: primary ? P.sheet : P.muted,
      opacity: disabled ? 0.4 : 1,
    }}>{children}</button>
  );
}

/** Validate one value against its field spec; returns an error string or ''. */
function fieldError(field, value) {
  if (value === '' || value == null) return '';
  if (field.control === 'select' || field.control === 'toggle') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'must be a number';
  if (field.min != null && n < field.min) return `must be ≥ ${field.min}`;
  if (field.max != null && n > field.max) return `must be ≤ ${field.max}`;
  return '';
}

function Field({ field, value, onChange, disabled = false }) {
  const { P, MO, inp } = useTheme();
  const err = fieldError(field, value);
  const roStyle = disabled ? { opacity: 0.55, cursor: 'not-allowed' } : null;
  const labelEl = (
    <span style={{ fontFamily: MO, fontSize: 11, color: P.muted }}>{field.label}</span>
  );

  let control;
  if (field.control === 'select') {
    control = (
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} style={{ ...inp, ...roStyle }}>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (field.control === 'toggle') {
    control = (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
        <span style={{ fontFamily: MO, fontSize: 12, color: P.ink }}>{value ? 'On' : 'Off'}</span>
      </label>
    );
  } else if (field.control === 'slider') {
    control = (
      <div style={{ marginTop: 6 }}>
        <input type="range" min={field.min} max={field.max} step={field.step}
          value={value ?? field.min} onChange={(e) => onChange(parseFloat(e.target.value))}
          disabled={disabled} style={{ width: '100%', ...roStyle }} />
        <span style={{ fontFamily: MO, fontSize: 11, color: P.ink }}>{value ?? '—'}</span>
      </div>
    );
  } else {
    control = (
      <input type="number" min={field.min} max={field.max} step={field.step || 'any'}
        value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        disabled={disabled}
        style={{ ...inp, borderColor: err ? P.danger : P.rule, ...roStyle }} />
    );
  }

  return (
    <div style={{ flex: '1 1 220px', marginBottom: 4 }}>
      <label>{labelEl}{control}</label>
      {err && <div style={{ fontFamily: MO, fontSize: 10, color: P.danger, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

export default function PlannerSettings({
  config, draft, setDraft, busy, error, savedAt,
  onSave, onReset, onReload, advancedOpen, setAdvancedOpen,
  canEdit = true, signedIn = false,
}) {
  const { P, FN, MO } = useTheme();
  // PR ④ §4: shop config is admin-only once signed in. readOnly ⇒ disable every
  // input and the Save/Reset actions; Reload stays available to anyone.
  const readOnly = !canEdit;
  const card = { background: P.surface, border: `0.5px solid ${P.rule}`, borderRadius: 10, padding: 16, marginBottom: 14 };
  const eyebrow = { fontFamily: MO, fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: P.dim };
  if (!config && !error) {
    return <div style={{ fontFamily: MO, fontSize: 12, color: P.dim }}>Loading planner settings…</div>;
  }

  const basic = CONFIG_FIELDS.filter((f) => f.group === 'basic');
  const advanced = CONFIG_FIELDS.filter((f) => f.group === 'advanced');
  const set = (key, v) => setDraft({ ...draft, [key]: v });

  // Any client-side range error blocks save (avoids a guaranteed 422).
  const anyError = CONFIG_FIELDS.some((f) => fieldError(f, draft?.[f.key]));
  // Has the draft diverged from the loaded config?
  const dirty = config && draft && CONFIG_FIELDS.some((f) => draft[f.key] !== config[f.key]);

  return (
    <div>
      <p style={{ fontFamily: FN, fontSize: 12, color: P.muted, margin: '0 0 16px', lineHeight: 1.5, maxWidth: 620 }}>
        Shop-wide planner defaults — these affect <strong>every future plan</strong>. To override
        for a single job, use the per-job options on the planning screen instead (they don't change
        these defaults). Note: config currently persists for the running server only and resets on
        restart (durable persistence is a planned backend task).
      </p>

      {readOnly && (
        <div style={{
          marginBottom: 14, padding: '10px 14px', borderRadius: 8,
          background: P.warnSoft, border: `0.5px solid ${P.warnLine}`,
          fontFamily: FN, fontSize: 12, color: P.ink, lineHeight: 1.5,
        }}>
          <span style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 0.8, textTransform: 'uppercase', color: P.warn, marginRight: 8 }}>Read-only</span>
          {signedIn
            ? 'Admin required to change shop config. You can view the current strategy but not edit it.'
            : 'Sign in as an admin to change shop config.'}
        </div>
      )}

      {error && (
        <div style={{
          marginBottom: 14, padding: '12px 14px', borderRadius: 8,
          background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
          fontFamily: FN, fontSize: 12, color: P.ink, whiteSpace: 'pre-line',
        }}>{error}</div>
      )}

      {/* Everyday fields + advanced */}
      <div style={card}>
        <div style={{ ...eyebrow, marginBottom: 12 }}>
          Tolerances &amp; objective
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 4 }}>
          {basic.map((f) => <Field key={f.key} field={f} value={draft?.[f.key]} onChange={(v) => set(f.key, v)} disabled={readOnly} />)}
        </div>

        {/* Advanced, collapsed by default */}
        <button onClick={() => setAdvancedOpen(!advancedOpen)} style={{
          fontFamily: MO, fontSize: 11, color: P.muted, background: 'transparent',
          border: 'none', cursor: 'pointer', padding: 0, marginTop: 16, marginBottom: advancedOpen ? 12 : 0,
        }}>
          {advancedOpen ? '▾' : '▸'} Advanced — search tuning
        </button>
        {advancedOpen && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {advanced.map((f) => <Field key={f.key} field={f} value={draft?.[f.key]} onChange={(v) => set(f.key, v)} disabled={readOnly} />)}
          </div>
        )}
      </div>

      {/* Objective-commits-provisional note (brief §4.2). */}
      {dirty && draft?.objective !== config?.objective && (
        <div style={{ fontFamily: FN, fontSize: 11, color: P.muted, marginBottom: 12 }}>
          Setting an objective here commits the shop's ranking preference — the "ranking being
          finalised" hint will stop showing on future plans.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn onClick={onSave} disabled={readOnly || busy || anyError || !dirty}>
          {busy ? 'Saving…' : 'Save settings'}
        </Btn>
        <Btn primary={false} onClick={onReload} disabled={busy}>Reload</Btn>
        <Btn primary={false} onClick={onReset} disabled={readOnly || busy}>Reset to defaults</Btn>
        {anyError && !readOnly && <span style={{ fontFamily: MO, fontSize: 10, color: P.danger }}>Fix the highlighted fields first.</span>}
        {!readOnly && !anyError && !dirty && savedAt && (
          <span style={{ fontFamily: MO, fontSize: 10, color: P.ok }}>Saved.</span>
        )}
      </div>
    </div>
  );
}
